import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Button, Typography, notification, Modal } from 'antd';

import { intialPaginationState, showingPageTotalRange, unexpectedError, PictureCell, filter, onlyYear } from '../../../common_functions';

const MAX_SELECTION = 10;

const Bibliography = props => {
  const { id: reportType } = useParams();
  const { getItems } = props;

  const [reports, setReports] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [sorter, setSorter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    getObjectsData(pagination);
  }, []);

  const getObjectsData = async (paginationObj, filters = {}, sorterObj = '') => {
    try {
      setLoading(true);

      const response = await getItems('books', {
        fields: ['*', 'artist.First_Name', 'artist.last_name'],
        sort: sorterObj && sorterObj.field ? (sorterObj.order === 'ascend' ? '' : '-') + sorterObj.field : '-id',
        filters: filters,
        offset: paginationObj.pageSize * (paginationObj.current - 1),
        limit: paginationObj.pageSize,
        meta: 'filter_count',
      });

      setPagination({
        ...paginationObj,
        total: response.meta.filter_count,
      });

      setReports(response.data);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (paginationObj, filters, sorterObj) => {
    setPagination(paginationObj);
    setSorter(sorterObj);

    const filtersItems = {};
    for (let key in filters) {
      if (filters[key]) {
        filtersItems[key] = { _icontains: filters[key][0] };
      }
    }

    setAppliedFilters(filtersItems);
    await getObjectsData(paginationObj, filtersItems, sorterObj);
  };

  const generatePDF = async () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({ message: 'Please select at least one record.' });
      return;
    }
    if (selectedRowKeys.length > MAX_SELECTION) {
      notification.warning({ message: `You can select a maximum of ${MAX_SELECTION} records.` });
      return;
    }
    try {
      setLoading(true);
      await props.BibliographyBooksReport({ selectedIds: selectedRowKeys });
      setSelectedRowKeys([]);
      notification.success({
        message: 'Report Queued',
        description: 'Your report is being generated. You will be notified by email when it is ready. Download it from Report History.',
        duration: 6,
      });
    } catch (error) {
      console.error(error);
      notification.error({ message: 'Failed to queue report. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const buildCsvRows = items =>
    items.map(item => ({
      Book_Title: item.book_title || '',
      Artist_Name: item.artist ? `${item.artist.First_Name || ''} ${item.artist.last_name || ''}`.trim() : '',
      Publisher: item.publisher || '',
      Author: item.author || '',
      Editor: item.editor || '',
      Edition: item.edition || '',
      ISBN: item.isbn || '',
      Date_of_Publication: onlyYear(item.date_of_publication) || '',
    }));

  const triggerCsvDownload = (csvData, filename) => {
    if (!csvData.length) {
      notification.warning({ message: 'No data available to download' });
      return;
    }
    const headers = Object.keys(csvData[0]).join(',');
    const rows = csvData.map(row =>
      Object.values(row)
        .map(value => `"${String(value).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadAllCSV = async () => {
    try {
      setLoading(true);
      const response = await getItems('books', {
        fields: ['*', 'artist.First_Name', 'artist.last_name'],
        sort: sorter?.field ? (sorter.order === 'ascend' ? '' : '-') + sorter.field : '-id',
        filters: appliedFilters,
        limit: -1,
      });
      triggerCsvDownload(buildCsvRows(response.data || []), 'bibliography_report.csv');
    } catch (error) {
      console.error(error);
      notification.error({ message: 'Failed to download report' });
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    if (selectedRowKeys.length > 0) {
      Modal.confirm({
        title: 'Download CSV',
        content: 'You have selected records. Do you want to download only the selected records or all records?',
        okText: 'Selected Only',
        cancelText: 'All Records',
        cancelButtonProps: {
          style: { backgroundColor: '#a6a300', borderColor: '#a6a300', color: '#fff' },
        },
        onOk: () => {
          const selectedData = reports.filter(item => selectedRowKeys.includes(item.id));
          if (!selectedData.length) {
            notification.warning({ message: 'No selected data found on this page.' });
            return;
          }
          triggerCsvDownload(buildCsvRows(selectedData), 'bibliography_selected.csv');
        },
        onCancel: () => downloadAllCSV(),
      });
      return;
    }
    await downloadAllCSV();
  };

  const columns = [
    {
      title: 'Sr. No.',
      key: 'sr_no',
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Cover Image',
      dataIndex: 'cover_photo',
      key: 'cover_photo',
      render: text => (text ? <PictureCell dataIndexValue={text} getFile={props.getFile} width={50} /> : '-'),
    },
    {
      title: 'Book Title',
      dataIndex: 'book_title',
      key: 'book_title',
      sorter: true,
      ...filter('book_title'),
    },
    {
      title: 'Artist',
      dataIndex: 'artist',
      key: 'artist',
      sorter: true,
      render: (_, record) => (record.artist ? `${record.artist.First_Name || ''} ${record.artist.last_name || ''}`.trim() : '-'),
      ...filter('artist'),
    },
    {
      title: 'Publisher',
      dataIndex: 'publisher',
      key: 'publisher',
      sorter: true,
      ...filter('publisher'),
      render: value => value || '-',
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      sorter: true,
      ...filter('author'),
      render: value => value || '-',
    },
    {
      title: 'Editor',
      dataIndex: 'editor',
      key: 'editor',
      sorter: true,
      ...filter('editor'),
      render: value => value || '-',
    },
    {
      title: 'Edition',
      dataIndex: 'edition',
      key: 'edition',
      sorter: true,
      ...filter('edition'),
      render: value => value || '-',
    },
    {
      title: 'ISBN',
      dataIndex: 'isbn',
      key: 'isbn',
      ...filter('isbn'),
      render: value => value || '-',
    },
    {
      title: 'Publication Year',
      dataIndex: 'date_of_publication',
      key: 'date_of_publication',
      sorter: true,
      render: value => onlyYear(value) || '-',
    },
  ];

  return (
    <Row className={loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: `/reports/bibliography-report/${reportType}` }, { title: "Bibliography" }]} />
      </Col>

      <Col className="white-paper" span={24}>
        <Row justify="space-between" align="middle">
          <Col xs={12}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Bibliography
            </Typography.Title>
          </Col>
          <Col xs={12} justify="end" align="right">
            <Button
              style={{ marginRight: '8px' }}
              icon={<FilePdfOutlined />}
              type="primary"
              size="small"
              onClick={generatePDF}
              disabled={selectedRowKeys.length === 0 || selectedRowKeys.length > MAX_SELECTION}
            >
              Generate PDF
            </Button>
            <Button
              icon={<DownloadOutlined />}
              type="primary"
              size="small"
              onClick={downloadCSV}
              style={{ backgroundColor: '#a6a300', borderColor: '#a6a300', color: '#fff' }}
            >
              Download CSV
            </Button>
          </Col>
        </Row>

        <div style={{ marginTop: 20 }}>
          <Table
            className="no-fixed-last-col"
            scroll={{ x: 'max-content' }}
            columns={columns}
            bordered
            dataSource={reports}
            pagination={{
              ...pagination,
              ...showingPageTotalRange,
            }}
            loading={loading}
            onChange={handleTableChange}
            rowKey="id"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (newSelectedKeys) => {
                const currentPageIds = reports.map(r => r.id);
                const merged = [
                  ...selectedRowKeys.filter(id => !currentPageIds.includes(id)),
                  ...newSelectedKeys,
                ];
                if (merged.length <= MAX_SELECTION) {
                  setSelectedRowKeys(merged);
                } else {
                  notification.warning({ message: `You can select a maximum of ${MAX_SELECTION} records.` });
                }
              },
              getCheckboxProps: record => ({
                disabled: selectedRowKeys.length >= MAX_SELECTION && !selectedRowKeys.includes(record.id),
                checked: selectedRowKeys.includes(record.id),
              }),
            }}
          />
        </div>
      </Col>
    </Row>
  );
};

export default Bibliography;
